import { FC, useCallback, useMemo, useState, useEffect } from 'react'
import styles from 'components/features/future/FutureNetworthForecastChart/FutureNetworthForecastChart.styl'
import { useStoreSelector } from 'helpers/reactReduxHelpers'
import { SizeHandler } from 'components/features/pfm/SizeHandler/SizeHandler'
import { calcChart } from 'components/features/external/PublicSpendingTrend/helpers'
import { LineChartAxis } from 'components/features/external/PublicSpendingTrend/LineChartAxis'
import { LineChartPoints } from 'components/features/external/PublicSpendingTrend/LineChartPoints'
import { LineChartPath } from 'components/features/external/PublicSpendingTrend/LineChartPath'
import { FutureLineChartSeries } from 'components/features/future/FutureLineChartSeries/FutureLineChartSeries'
import { LineChartInteractionHandler } from 'components/features/pfm/LineChartInteractionHandler/LineChartInteractionHandler'
import { LineChartHover } from 'components/features/external/PublicSpendingTrend/LineChartHover'
import { formatUSCurrency } from 'helpers/formatHelpers'
import { getAgeSelector } from 'selectors/globalSelectors'
import { networthForecastSelector, chartDefaultIndexSelector } from 'components/features/future/selectors'

type FutureNetworthForecastChartProps = {
  onHighlightChange?: (index: number) => void
}

export const FutureNetworthForecastChart: FC<FutureNetworthForecastChartProps> = (props) => {
  const { onHighlightChange } = props

  const getAge = useStoreSelector(getAgeSelector)
  const items = useStoreSelector(networthForecastSelector)

  const defaultIndex = useStoreSelector(chartDefaultIndexSelector)

  const [highlightIndex, setHighlightIndex] = useState(defaultIndex)

  useEffect(() => {
    setHighlightIndex(defaultIndex)
  }, [defaultIndex])

  const handleIndexChange = useCallback<(index: number) => void>((index) => {
    const lastIndex = items.length - 1
    const nextIndex = Math.min(lastIndex, index)
    setHighlightIndex(nextIndex)
    onHighlightChange && onHighlightChange(nextIndex)
  }, [items.length, onHighlightChange])

  const handleMouseLeave = useCallback(() => {
    setHighlightIndex(defaultIndex)
  }, [defaultIndex])

  const [boundRect, setBoundRect] = useState<ClientRect | null>(null)
  const {
    width = null,
    height = null,
  } = boundRect || {}
  const handleChange = useCallback<(rect: ClientRect) => void>((rect) => {
    setBoundRect(rect)
  }, [])

  const { points, zeroAbscissa } = useMemo(() => {
    if (!width || !height || !items || items.length === 0) {
      return { points: null, zeroAbscissa: null }
    }

    return calcChart(items, width, height, { noGap: true })
  }, [items, width, height])

  const chartChild = useMemo(() => {
    if (points === null || zeroAbscissa === null || width === null || height === null) {
      return (
        <div className={ styles.chartLayout }>
          <SizeHandler onChange={ handleChange } className={ styles.chartWrapper }>
            <div className={ styles.chart } />
            <br />
          </SizeHandler>
        </div>
      )
    }

    return (
      <LineChartInteractionHandler
        className={ styles.chartLayout }
        count={ points.length }
        width={ width }
        onChange={ handleIndexChange }
        onMouseLeave={ handleMouseLeave }
      >
        <SizeHandler onChange={ handleChange } className={ styles.chartWrapper }>
          <svg
            className={ styles.chart }
          >
            <LineChartAxis
              points={ points }
              zero={ zeroAbscissa }
              width={ width }
            />
            <LineChartPath
              points={ points }
              zero={ zeroAbscissa }
            />
            <LineChartHover
              points={ points }
              index={ highlightIndex }
              height={ height }
              width={ width }
            />
            <LineChartPoints
              points={ points }
            />
          </svg>
        </SizeHandler>
        <div className={ styles.series }>
          <FutureLineChartSeries
            items={ items }
            index={ highlightIndex }
            width={ width }
          />
        </div>
      </LineChartInteractionHandler>
    )
  }, [points, width, zeroAbscissa, handleChange, items, handleIndexChange, height, highlightIndex, handleMouseLeave])

  const headerChild = useMemo(() => {
    const index = highlightIndex !== -1 ? highlightIndex : items.length - 1
    const item = items[index]
    if (!item || !getAge) {
      return null
    }

    return (
      <div className={ styles.header }>
        <div className={ styles.age }>
          AGE { getAge(item.date) }
        </div>
        <div className={ styles.sum }>
          { formatUSCurrency(item.amount) }
        </div>
      </div>
    )
  }, [getAge, highlightIndex, items])

  if (!chartChild) {
    return null
  }

  return (
    <div className={ styles.futureNetworthForecastChart }>
      { headerChild }
      { chartChild }
    </div>
  )
}
